import { useVendorNotPermitted } from '@melio/ap-domain';
import { EbillCellBadge, LockedVendorIcon, VendorAvatar } from '@melio/ap-widgets';
import { Group, Link, Table, useBreakpoint } from '@melio/penny';
import { useAnalytics } from '@melio/platform-analytics';
import { Payment, VendorEBillStatusEnum } from '@melio/platform-api';
import { useMelioIntl } from '@melio/platform-i18n';
import { useLocation, useNavigate } from 'react-router-dom';

export type PaymentsTabVendorNameCellProps = {
  payment: Payment;
  onEBillSubscribeClick: (vendorId: string) => void;
  onAutoPayActivationClick: (vendorId: string) => void;
};
export const PaymentsTabVendorNameCell = ({
  payment,
  onEBillSubscribeClick,
  onAutoPayActivationClick,
}: PaymentsTabVendorNameCellProps) => {
  const navigate = useNavigate();
  const { isExtraSmallScreen } = useBreakpoint();
  const { track } = useAnalytics();
  const { search } = useLocation();
  const isVendorNotPermitted = useVendorNotPermitted(payment.vendor);
  const { formatMessage } = useMelioIntl();

  const onVendorNameClick = (e: React.MouseEvent<HTMLLinkElement>) => {
    e.preventDefault();
    e.stopPropagation();

    track('Dashboard', 'Click', {
      Intent: 'vendor-details',
      Cta: 'vendor-name-click',
    });

    return navigate({ pathname: `vendor/${payment.vendorId}`, search });
  };

  const vendorName = payment.vendor?.name ?? '';
  const vendorExternalLogoUrl = payment.vendor?.externalLogoUrl;
  const isManaged = payment.vendor?.isManaged;

  return (
    <Table.Cell data-testid={`payments-tab-row-${payment.id}-vendor-name-cell`}>
      <Group alignItems="center">
        {!isExtraSmallScreen && (
          <VendorAvatar name={vendorName || '?'} logoUrl={vendorExternalLogoUrl} isManaged={isManaged} />
        )}
        <Group alignItems="center" spacing="xxs">
          {isVendorNotPermitted ? <LockedVendorIcon /> : null}
          <Link
            label={vendorName}
            data-testid={`payments-tab-row-${payment.id}-vendor-name-cell-button`}
            href={`vendor/${payment.vendorId}`}
            shouldSupportEllipsis
            onClick={onVendorNameClick}
            aria-label={formatMessage('activities.payDashboard.table.vendorName.cell.vendorView.link.ariaLabel', {
              label: vendorName,
            })}
          />
          {payment.vendor && payment.vendor.eBillStatus !== VendorEBillStatusEnum.NotCapable && (
            <EbillCellBadge
              vendor={payment.vendor}
              onEBillSubscribeClick={onEBillSubscribeClick}
              onAutoPayActivationClick={onAutoPayActivationClick}
              isReadOnly={isVendorNotPermitted}
            />
          )}
        </Group>
      </Group>
    </Table.Cell>
  );
};
